import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  AGENT_REGISTRY,
  SCENARIO_TEMPLATES,
  executeAgentPipeline,
} from '../services/simonAI';
import ModelSelector from '../components/ModelSelector';

// ─── Types ───────────────────────────────────────────────────────────────────

type AgentStatus = 'idle' | 'running' | 'done';

interface AgentStateMap {
  [agentId: string]: AgentStatus;
}

interface TimelineItem {
  time: string;
  action: string;
  detail: string;
  agent?: string;
}

interface PlanResult {
  planId: string;
  title: string;
  summary: string;
  timeline: TimelineItem[];
  budget?: { total: number; breakdown?: Record<string, number>; withinBudget?: boolean };
  weather?: { forecast: string; outdoorSuitability?: string; suggestion?: string };
  simulation?: { overallScore: number; recommendation: string };
  actionable?: { canBook: boolean; bookTime: string };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 6) return '夜已深，愿你安好';
  if (h < 12) return '新的一天即将开始';
  if (h < 14) return '午间小憩时光';
  if (h < 18) return '下午好';
  if (h < 22) return '夜幕将至，月光明亮';
  return '夜深了，好好休息';
}

function getTimeOfDay(): string {
  return new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

const PLACEHOLDER_EXAMPLES = [
  '周六晚上和女朋友约会，预算500元，想在市中心找一家氛围好的日料餐厅...',
  '下周三去北京出差两天，需要安排机票、酒店、商务晚宴，预算3000元',
  '周日带5岁孩子去公园玩耍，预算200元，需要午餐和下午茶安排',
  '想给男朋友买一款5000元左右的机械键盘，程序员用，红轴静音款',
];


// ─── Sub-Components ──────────────────────────────────────────────────────────

function ScenarioCard({
  template,
  onClick,
  index,
}: {
  template: (typeof SCENARIO_TEMPLATES)[0];
  onClick: (input: string) => void;
  index: number;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={() => onClick(template.input)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 8,
        padding: '18px 20px',
        background: hovered ? 'rgba(15, 37, 65, 0.85)' : 'var(--surface-1)',
        border: `1px solid ${hovered ? 'var(--border-hover)' : 'var(--border-default)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        transition: 'all var(--duration-normal) var(--ease)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.2)' : 'none',
        animation: `fadeUp var(--duration-slow) var(--ease) ${index * 80}ms both`,
        textAlign: 'left',
        width: '100%',
        color: 'inherit',
        fontFamily: 'inherit',
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1 }}>{template.icon}</span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--text-primary)',
        }}
      >
        {template.label}
      </span>
      <span
        style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {template.input.slice(0, 40)}...
      </span>
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          fontSize: 10,
          color: 'var(--text-muted)',
          opacity: 0.5,
        }}
      >
        {template.agents.length} Agents
      </div>
    </button>
  );
}

function AgentNode({
  agentId,
  status,
}: {
  agentId: string;
  status: AgentStatus;
}) {
  const agent = AGENT_REGISTRY[agentId];
  if (!agent) return null;

  const isRunning = status === 'running';
  const isDone = status === 'done';
  const isIdle = status === 'idle';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        minWidth: 64,
        transition: 'all var(--duration-normal) var(--ease)',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          background: isDone
            ? 'rgba(52, 211, 153, 0.12)'
            : isRunning
              ? `${agent.color}18`
              : 'var(--glass-sm)',
          border: `2px solid ${isDone ? '#34d399' : isRunning ? agent.color : 'var(--border-default)'}`,
          boxShadow: isRunning ? `0 0 16px ${agent.color}30` : 'none',
          transition: 'all var(--duration-normal) var(--ease)',
          animation: isRunning ? 'agentPulse 1.5s var(--ease) infinite' : 'none',
          position: 'relative',
          filter: isIdle ? 'grayscale(0.6)' : 'none',
          opacity: isIdle ? 0.4 : 1,
        }}
      >
        <span>{isDone ? '✓' : agent.icon}</span>
        {isRunning && (
          <div
            style={{
              position: 'absolute',
              inset: -3,
              borderRadius: '50%',
              border: `2px solid ${agent.color}`,
              opacity: 0.4,
              animation: 'agentRipple 1.5s ease-out infinite',
            }}
          />
        )}
      </div>
      <span
        style={{
          fontSize: 10,
          color: isDone ? '#34d399' : isRunning ? agent.color : 'var(--text-muted)',
          fontWeight: isRunning ? 600 : 400,
          textAlign: 'center',
          lineHeight: 1.3,
          maxWidth: 64,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          transition: 'color var(--duration-fast) var(--ease)',
        }}
      >
        {agent.name.replace(' Agent', '')}
      </span>
    </div>
  );
}

function PipelineAnimation({ agentStates }: { agentStates: AgentStateMap }) {
  const activeAgents = useMemo(() => {
    return Object.entries(agentStates)
      .filter(([_, s]) => s !== 'idle')
      .map(([id]) => id);
  }, [agentStates]);

  if (activeAgents.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '20px 24px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-1)',
        border: '1px solid var(--border-default)',
        animation: 'fadeUp var(--duration-normal) var(--ease) both',
        width: '100%',
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          fontWeight: 500,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        Agent Pipeline 运行中
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 16,
          rowGap: 12,
        }}
      >
        {Object.keys(AGENT_REGISTRY).map((id) => (
          <AgentNode key={id} agentId={id} status={agentStates[id] || 'idle'} />
        ))}
      </div>
      <div
        style={{
          width: '100%',
          height: 2,
          background: 'var(--glass-sm)',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${(Object.values(agentStates).filter((s) => s === 'done').length / Object.keys(AGENT_REGISTRY).length) * 100}%`,
            background: 'linear-gradient(90deg, var(--accent), #34d399)',
            borderRadius: 2,
            transition: 'width var(--duration-normal) var(--ease)',
          }}
        />
      </div>
    </div>
  );
}

function TimelineResult({ plan }: { plan: PlanResult }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        padding: '20px 24px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-1)',
        border: '1px solid var(--border-default)',
        animation: 'fadeUp var(--duration-slow) var(--ease) both',
        width: '100%',
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 4,
          }}
        >
          {plan.title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}
        >
          {plan.summary}
        </div>
      </div>

      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: 28 }}>
        {plan.timeline.map((item, idx) => {
          const agent = item.agent ? AGENT_REGISTRY[item.agent] : null;
          const isLast = idx === plan.timeline.length - 1;
          return (
            <div key={idx} style={{ position: 'relative', paddingBottom: isLast ? 0 : 20 }}>
              {/* Dot */}
              <div
                style={{
                  position: 'absolute',
                  left: -22,
                  top: 4,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: agent ? agent.color : 'var(--accent)',
                  boxShadow: agent ? `0 0 8px ${agent.color}40` : '0 0 8px var(--accent-glow)',
                }}
              />
              {/* Line */}
              {!isLast && (
                <div
                  style={{
                    position: 'absolute',
                    left: -18,
                    top: 18,
                    width: 2,
                    height: 'calc(100% - 8px)',
                    background: 'linear-gradient(to bottom, var(--border-hover), transparent)',
                  }}
                />
              )}
              {/* Content */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 2,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--accent)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {item.time}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {item.action}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                  }}
                >
                  {item.detail}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Meta row */}
      {(plan.budget || plan.simulation) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid var(--border-subtle)',
          }}
        >
          {plan.budget && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            >
              <span style={{ fontSize: 14 }}>💰</span>
              <span>预算 ¥{plan.budget.total}</span>
              {plan.budget.withinBudget && (
                <span
                  style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-pill)',
                    background: 'rgba(52, 211, 153, 0.1)',
                    color: '#34d399',
                  }}
                >
                  预算内
                </span>
              )}
            </div>
          )}
          {plan.simulation && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--text-secondary)',
              }}
            >
              <span style={{ fontSize: 14 }}>🔬</span>
              <span>仿真评分 {plan.simulation.overallScore}/100</span>
            </div>
          )}
          {plan.actionable?.canBook && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--accent)',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 14 }}>📅</span>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--accent-soft)',
                  border: '1px solid rgba(232, 210, 138, 0.2)',
                }}
              >
                预约 {plan.actionable.bookTime}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AgentMatrix() {
  const agents = useMemo(() => Object.values(AGENT_REGISTRY), []);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
        gap: 8,
        padding: '12px 16px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-2)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      {agents.map((agent) => (
        <div
          key={agent.id}
          title={`${agent.name} · ${agent.desc}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            padding: '8px 4px',
            borderRadius: 'var(--radius-sm)',
            transition: 'background var(--duration-fast) var(--ease)',
            cursor: 'default',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = 'var(--glass-md)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>{agent.icon}</span>
          <span
            style={{
              fontSize: 9,
              color: 'var(--text-muted)',
              textAlign: 'center',
              lineHeight: 1.2,
              maxWidth: 52,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {agent.name.replace(' Agent', '')}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AICommandCenter() {
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [agentStates, setAgentStates] = useState<AgentStateMap>({});
  const [planResult, setPlanResult] = useState<PlanResult | null>(null);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Rotate placeholder examples
  useEffect(() => {
    const timer = setInterval(() => {
      setPlaceholderIdx((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Scroll to result when ready
  useEffect(() => {
    if (planResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [planResult]);

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isProcessing) return;

    setIsProcessing(true);
    setPlanResult(null);

    // Initialize all agents to idle
    const initialStates: AgentStateMap = {};
    Object.keys(AGENT_REGISTRY).forEach((id) => {
      initialStates[id] = 'idle';
    });
    setAgentStates({ ...initialStates });

    try {
      const result = await executeAgentPipeline(
        input.trim(),
        (agentId) => {
          setAgentStates((prev) => ({ ...prev, [agentId]: 'running' }));
        },
        (agentId, _resultData) => {
          setAgentStates((prev) => ({ ...prev, [agentId]: 'done' }));
        },
      );
      setPlanResult(result as PlanResult);
    } catch (err) {
      console.error('Agent pipeline failed:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [input, isProcessing]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleScenarioClick = useCallback(
    (scenarioInput: string) => {
      setInput(scenarioInput);
      inputRef.current?.focus();
    },
    [],
  );

  const handleInputFocus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div style={styles.pageShell}>
      {/* Keyframe styles */}
      <style>{`
        @keyframes agentPulse {
          0%, 100% { box-shadow: 0 0 8px ${'var(--accent-glow)'}(0,0,0); }
          50% { box-shadow: 0 0 20px rgba(232, 210, 138, 0.2); }
        }
        @keyframes agentRipple {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
      `}</style>

      {/* Background ambient glow */}
      <div style={styles.ambientBg} />

      {/* ── Main Content ── */}
      <div style={styles.mainContent}>
        {/* ── Top bar: model switcher ── */}
        <div style={styles.modelBar}>
          <ModelSelector />
        </div>

        {/* ── Welcome Area ── */}
        <header style={styles.welcomeArea}>
          <h2 style={styles.welcomeTitle}>Hi，{getGreeting()}</h2>
          <p style={styles.welcomeSub}>今天想要做什么？</p>
        </header>

        {/* ── Center Input Area ── */}
        <div style={styles.inputArea}>
          <div style={styles.inputWrapper} onClick={handleInputFocus}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={PLACEHOLDER_EXAMPLES[placeholderIdx]}
              disabled={isProcessing}
              rows={2}
              style={styles.input}
            />
            <button
              onClick={handleSubmit}
              disabled={!input.trim() || isProcessing}
              style={{
                ...styles.sendBtn,
                opacity: isProcessing ? 0.5 : input.trim() ? 1 : 0.3,
                cursor: isProcessing ? 'wait' : input.trim() ? 'pointer' : 'default',
              }}
            >
              {isProcessing ? (
                <span style={{ animation: 'gentlePulse 1s ease-in-out infinite' }}>
                  ⚡
                </span>
              ) : (
                '↵'
              )}
            </button>
          </div>
        </div>

        {/* ── Quick Scenario Cards ── */}
        <div style={styles.scenarioRow}>
          {SCENARIO_TEMPLATES.map((template, idx) => (
            <ScenarioCard
              key={template.id}
              template={template}
              onClick={handleScenarioClick}
              index={idx}
            />
          ))}
        </div>

        {/* ── Pipeline Animation & Results ── */}
        <div ref={resultRef} style={styles.resultArea}>
          {isProcessing && (
            <div style={{ width: '100%', animation: 'fadeUp var(--duration-normal) var(--ease) both' }}>
              <PipelineAnimation agentStates={agentStates} />
            </div>
          )}
          {planResult && !isProcessing && <TimelineResult plan={planResult} />}
        </div>

        {/* ── Bottom Agent Matrix ── */}
        <footer style={styles.footer}>
          <div style={styles.footerLabel}>
            <span
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#34d399',
                marginRight: 6,
                animation: 'gentlePulse 2s ease-in-out infinite',
              }}
            />
            {Object.keys(AGENT_REGISTRY).length} Agents 在线
          </div>
          <AgentMatrix />
        </footer>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  pageShell: {
    position: 'relative',
    width: '100%',
    minHeight: '100vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    /* 透出 #root::before 的森林背景（登录页原图） */
    background: 'transparent',
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-primary)',
  },
  ambientBg: {
    position: 'fixed',
    inset: 0,
    background:
      'radial-gradient(ellipse 50% 30% at 50% 0%, rgba(232, 210, 138, 0.03) 0%, transparent 70%), radial-gradient(ellipse 30% 20% at 80% 90%, rgba(168, 199, 216, 0.02) 0%, transparent 70%)',
    pointerEvents: 'none' as const,
  },
  mainContent: {
    flex: 1,
    width: '100%',
    maxWidth: 780,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 28,
    padding: '0 24px 32px',
    position: 'relative',
    zIndex: 1,
  },
  modelBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 16,
  },
  welcomeArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    animation: 'fadeUp var(--duration-slow) var(--ease) both',
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: 300,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    margin: 0,
  },
  welcomeSub: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    margin: 0,
    fontWeight: 400,
  },
  inputArea: {
    width: '100%',
    maxWidth: 580,
    animation: 'fadeUp var(--duration-slow) var(--ease) 100ms both',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    transition: 'box-shadow var(--duration-normal) var(--ease), border-color var(--duration-normal) var(--ease)',
  },
  input: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'var(--text-primary)',
    fontSize: 15,
    lineHeight: 1.5,
    padding: '16px 56px 16px 20px',
    resize: 'none' as const,
    fontFamily: 'inherit',
    minHeight: 52,
    maxHeight: 100,
  },
  sendBtn: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '1px solid var(--border-default)',
    background: 'var(--accent-soft)',
    color: 'var(--accent)',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease)',
  },
  scenarioRow: {
    width: '100%',
    maxWidth: 580,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 10,
    animation: 'fadeUp var(--duration-slow) var(--ease) 200ms both',
  },
  resultArea: {
    width: '100%',
    maxWidth: 580,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  footer: {
    position: 'relative',
    width: '100%',
    maxWidth: 580,
    padding: '12px 0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    animation: 'fadeUp var(--duration-slow) var(--ease) 300ms both',
  },
  footerLabel: {
    fontSize: 10,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    display: 'flex',
    alignItems: 'center',
  },
};
